import React, { useState, useRef, useEffect } from 'react';
import { 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  IconButton, 
  Typography, 
  Paper,
  Box,
  Input
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  DragHandle as DragHandleIcon, 
  InsertDriveFile as FileIcon, 
  Image as ImageIcon, 
  AudioFile as MusicIcon, 
  PlayArrow as PlayIcon, 
  Pause as PauseIcon,
  AddPhotoAlternate as AddPhotoIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';

const StyledListItem = styled(ListItem)(({ theme, isDragging }) => ({
  marginBottom: theme.spacing(1),
  backgroundColor: theme.palette.background.default,
  opacity: isDragging ? 0.5 : 1,
  cursor: 'move',
}));

const InsertIndicator = styled(Box)(({ theme }) => ({
  position: 'absolute',
  left: 0,
  right: 0,
  height: 2,
  backgroundColor: theme.palette.primary.main,
}));

const ThumbnailPreview = styled('img')({
  width: 50,
  height: 50,
  objectFit: 'cover',
  marginRight: 8,
});

const DropZone = styled(Box)(({ theme, isDragging }) => ({
  border: `2px dashed ${theme.palette.primary.main}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(2),
  textAlign: 'center',
  color: theme.palette.text.secondary,
  backgroundColor: isDragging ? theme.palette.action.hover : 'transparent',
  transition: theme.transitions.create('background-color'),
  marginBottom: theme.spacing(2),
}));

const FileReorderList = () => {
  const [files, setFiles] = useState([]);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [insertIndex, setInsertIndex] = useState(null);
  const [playingIndex, setPlayingIndex] = useState(null);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const listRef = useRef(null);
  const audioRefs = useRef({});
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  useEffect(() => {
    return () => {
      Object.values(audioRefs.current).forEach(audio => audio && audio.pause());
      files.forEach(file => URL.revokeObjectURL(file.url));
    };
  }, [files]);

  const onDragStart = (index, e) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const onDragOver = (e) => {
    e.preventDefault();
    if (draggedIndex === null || !listRef.current) return;
    const listRect = listRef.current.getBoundingClientRect();
    const y = e.clientY - listRect.top;
    const itemHeight = listRect.height / files.length;
    let index = Math.floor(y / itemHeight);
    const offset = y % itemHeight;
    if (offset > itemHeight / 2) index++;
    index = Math.max(0, Math.min(files.length, index));
    setInsertIndex(index);
  };

  const onDragEnd = () => {
    if (draggedIndex !== null && insertIndex !== null && draggedIndex !== insertIndex) {
      const newFiles = [...files];
      const [draggedFile] = newFiles.splice(draggedIndex, 1);
      newFiles.splice(insertIndex > draggedIndex ? insertIndex - 1 : insertIndex, 0, draggedFile);
      setFiles(newFiles);
    }
    setDraggedIndex(null);
    setInsertIndex(null);
  };

  const togglePlay = (index) => {
    const audio = audioRefs.current[index];
    if (!audio) return;

    if (playingIndex === index) {
      audio.pause();
      setPlayingIndex(null);
    } else {
      if (playingIndex !== null) {
        audioRefs.current[playingIndex].pause();
      }
      audio.play();
      setPlayingIndex(index);
    }
  };

  const getFileIcon = (fileType) => {
    if (fileType.startsWith('audio/')) return <MusicIcon color="primary" />;
    if (fileType.startsWith('image/')) return <ImageIcon color="secondary" />;
    return <FileIcon color="action" />;
  };

  const handleFileSelect = (selectedFiles) => {
    const newFiles = selectedFiles.map(file => ({
      id: `file-${Date.now()}-${Math.random()}`,
      name: file.name,
      type: file.type,
      url: URL.createObjectURL(file),
      file: file
    }));
    setFiles(prevFiles => [...prevFiles, ...newFiles]);
  };

  const handleInputFileSelect = (event) => {
    handleFileSelect(Array.from(event.target.files));
  };

  const handleThumbnailSelect = (index) => {
    fileInputRef.current.click();
    fileInputRef.current.onchange = (event) => {
      const file = event.target.files[0];
      if (file && file.type.startsWith('image/')) {
        const updatedFiles = [...files];
        updatedFiles[index] = {
          ...updatedFiles[index],
          thumbnail: URL.createObjectURL(file)
        };
        setFiles(updatedFiles);
      }
    };
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingFile(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingFile(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingFile(false);
    
    const droppedFiles = Array.from(e.dataTransfer.files);
    handleFileSelect(droppedFiles);
  };

  const handleRemoveFile = (index) => {
    setFiles(prevFiles => {
      const newFiles = [...prevFiles];
      const removedFile = newFiles.splice(index, 1)[0];
      URL.revokeObjectURL(removedFile.url);
      if (removedFile.thumbnail) {
        URL.revokeObjectURL(removedFile.thumbnail);
      }
      if (playingIndex === index) {
        setPlayingIndex(null);
      }
      return newFiles;
    });
  };

  return (
    <Paper elevation={3} sx={{ maxWidth: 'auto', mx: 'auto', mt: 4, p: 2 }}>
      <Typography variant="h5" gutterBottom>
        
      </Typography>
      <Input
          type="file"
          inputProps={{ multiple: true ,accept: "audio/*" }}
          onChange={handleInputFileSelect}
          sx={{ display: 'none' }}
          id="file-input"
        />
      <DropZone
        ref={dropZoneRef}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        isDragging={isDraggingFile}
      >
        <label htmlFor="file-input">
        <CloudUploadIcon sx={{ fontSize: 48, mb: 1 }} />
        <Typography>
          ドラッグ＆ドロップでファイルをアップロード
        </Typography>
        <Typography variant="body2">
          または
        </Typography>
        
          <Typography
            component="span"
            sx={{
              cursor: 'pointer',
              textDecoration: 'underline',
              color: 'primary.main',
            }}
          >
            ファイルを選択
          </Typography>
        </label>
      </DropZone>
      <List ref={listRef} onDragOver={onDragOver}>
        {files.map((file, index) => (
          <StyledListItem
            key={file.id}
            isDragging={draggedIndex === index}
            draggable
            onDragStart={(e) => onDragStart(index, e)}
            onDragEnd={onDragEnd}
          >
            <ListItemIcon>
              <DragHandleIcon />
            </ListItemIcon>
            {file.thumbnail ? (
              <ThumbnailPreview src={file.thumbnail} alt="Thumbnail" />
            ) : (
              <ListItemIcon>
                {getFileIcon(file.type)}
              </ListItemIcon>
            )}
            <ListItemText primary={file.name} />
            {file.type.startsWith('audio/') && (
              <IconButton
                onClick={() => togglePlay(index)}
                color="primary"
                size="small"
              >
                {playingIndex === index ? <PauseIcon /> : <PlayIcon />}
              </IconButton>
            )}
            <IconButton
              onClick={() => handleThumbnailSelect(index)}
              color="primary"
              size="small"
            >
              <AddPhotoIcon />
            </IconButton>
            <IconButton
              onClick={() => handleRemoveFile(index)}
              color="error"
              size="small"
            >
              <DeleteIcon />
            </IconButton>
            {file.type.startsWith('audio/') && (
              <audio
                ref={el => audioRefs.current[index] = el}
                src={file.url}
                onEnded={() => setPlayingIndex(null)}
              />
            )}
          </StyledListItem>
        ))}
        {insertIndex !== null && draggedIndex !== null && (
          <InsertIndicator
            sx={{
              top: `${insertIndex * (100 / files.length)}%`,
            }}
          />
        )}
      </List>
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept="image/*"
      />
    </Paper>
  );
};

export default FileReorderList;